import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {state} from 'lit/decorators/state.js';
import {when} from 'lit/directives/when.js';
import {classMap} from 'lit/directives/class-map.js';
import {navigator} from 'lit-element-router';
import {msg} from '@lit/localize';
import {v4 as uniqueId} from 'uuid';
import store from '../store';
import gear from '../../icons/gear';

import {login, silentLogin, logout} from '../slices/userSlice';
import {downloadProfile, downloadZip, downloadSimulationResult, setPicoPassword} from '../slices/lightProfileSlice';
import {routeUrl} from '../util/routeUrl';
import {setSettingsActive, toggleSettingsActive} from '../slices/statusSlice';

@customElement('login-pop-up')
export class LoginPopUp extends connect(store)(navigator(LitElement)) {

	@property()
	email = '';

	@property()
	password = '';

	static styles = [
		css`
			:host {
				display: flex;
				flex-direction: column;
				align-items: center;
			}

			.dropdown {
				position: relative;
				display: inline-block;

				font: var(--font-widget-title);
				color: var(--color-widget-border-light);
			}

			.dropdown-content {
				display: block;
				position: absolute;
				top: 60px;
				right: -73px;
				background-color: var(--color-grey6);
				color: var(--color-widget-border-light);
				z-index: 9999;
				margin-top: 0px;
				min-width: 280px;
			}
			@media (min-width: 768px) {
				.dropdown-content {
					top: 0;
					right: -8px;
				}
			}
			section {
				padding: var(--padding);
				border-bottom: 1px solid var(--color-black);
			}

			.downloadBtns {
				display: flex;
				gap: var(--small-padding);
			}

			.uploadSection {
				margin-top: var(--small-padding);
				max-width: 95%;
			}

			button {
				display: flex;
				background-color: transparent;
				box-shadow: none;
				border: 1px solid var(--color-widget-border-light);
				color: #fff;
				font-family: var(--font-family-light);
				font-size: 12px;
				font-style: normal;
				font-weight: 400;
				line-height: 19px;
				padding: 5px 10px;
				cursor: pointer;
				white-space: nowrap;
				width: 100%;
			}

			button.submit {
				background-color: transparent;
				border-radius: 0;
				border: 1px solid var(--color-widget-border-light);
				padding: 10px var(--column-gap);
				margin-top: var(--very-small-padding);
				width: 100%;
			}
			button.submit:disabled {
				background-color: transparent;
				border-color: var(--color-grey5);
				color: var(--color-widget-border-light);
			}

			input {
				font-family: var(--font-family-monospace);
				font-size: var(	--font-size-widget-small-mono);
				color: var(--color-widget-border-light);
				background-color: transparent;

				min-height: var(--height-widget);
				transition: background-color 0.4s, color 0.4s;
				padding: 0.5em 1em;
				line-height: 120%;
				margin: 0 0 var(--very-small-padding) 0;
				border: 1px solid var(--color-widget-border-light);
				width: 100%;
				box-sizing: border-box;
				text-align: left;
			}
			.loginFailed input {
				border-color: var(--color-warn);
			}
			p {
				margin: 0 0 var(--padding) 0;
			}
			.picopassword {
				display: flex;
				gap: var(--very-small-padding);
			}
			.picopassword input {
				margin: 0;
				flex-basis: 80%;
			}
			.picopassword button {
				margin: 0;
				flex-basis: 20%;
			}
		`
	];

	constructor() {
		super();
		if (this.getCookie('slxcfgsession')) {
			store.dispatch(silentLogin({}));
		}
		store.subscribe(() => this.requestUpdate());
	}
	connectedCallback() {
		super.connectedCallback();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
	}
	stateChanged(s) {
		this.userProfile = s.userProfile.data;
		this.info = s.configuration.data.projects[0].project.info;
		this.status = s.status;
		this.active = this.status.settingsActive;
		this.lightProfile = s.lightProfile.data;
		this.picoPassword = this.lightProfile?.project?.picoPassword;
	}

	hide() {
		store.dispatch(setSettingsActive({setSettingsActive: false}));
	}

	onLogin() {
		store.dispatch(login({
			userName: this.email,
			password: this.password
		}));
		this.hide();
	}
	onLogout() {
		store.dispatch(logout());
		this.hide();
	}

	handleInput(e) {
		this[e.target.id] = e.target.value;
		if(e.key === 'Enter') {
			this.onLogin();
		}
		this.requestUpdate();
	}
	onPicoPassword(e) {
		const pass = e.target.value;
		if(e.key === 'Enter') {
			store.dispatch(setPicoPassword(
				{
					password: pass,
				}
			));
		}
		this.requestUpdate();
	}
	onGeneratePassword(e) {
		const pass = uniqueId().slice(-10);
		store.dispatch(setPicoPassword(
			{
				password: pass,
			}
		));
		this.requestUpdate();
	}
	getCookie(name) {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) {
			return parts.pop().split(';').shift();
		} else {
			return false;
		}
	}
	async downloadProfile() {
		store.dispatch(downloadProfile());
	}
	async downloadZip() {
		store.dispatch(downloadZip(
			{
				info: this.info,
			}
		));
	}
	async downloadSimulationResult() {
		store.dispatch(downloadSimulationResult());
	}

	startNewConfiguration(e) {
		this.navigate(routeUrl({media: 'sections', key: 'new'}));
		window.location.reload();
	}

	render() {
		const loginEnabled = this.email && this.email !== '' && this.password && this.password !== '';

		return html`
			<div class="dropdown">
				<div class="dropdown-content">
					<section>
						<language-switch></language-switch>
					</section>
					<section>
						<button @click="${this.startNewConfiguration}">${msg('Start new configuration')}</button>
					</section>
					<section>
						<shortnumber-load></shortnumber-load>
					</section>
					${when(this.userProfile.loggedIn && this.status.family === 'LightProfile', () => html`
						<section>
							<mode-switch></mode-switch>
						</section>
						<section class="files">
						${when(this.userProfile.lightprofile_config_download, () => html`
								<div>
									<p>Download:</p>
									<div class="downloadBtns">
										<button @click="${this.downloadZip}">Download ZIP</button>
										<button @click="${this.downloadSimulationResult}">Download Simulation</button>
									</div>
								</div>
						`)}
						${when(this.userProfile.lightprofile_config_upload, () => html`
								<div class="uploadSection">
									<p>Upload:</p>
									<light-profile-drop></light-profile-drop>
								</div>
								`)}
						</section>
						<section>
							<label class="field-name">
								Pico ${msg('Password')}
								<div class="picopassword">
									<input type="text" name="conf9pass" id="conf9pass" .value=${this.picoPassword} @input=${this.onPicoPassword} @keypress=${this.onPicoPassword}>
									<button class="submit" @click=${this.onGeneratePassword}>${msg('new')}</button>
								</div>
							</label>
						</section>
					`)}
					${when(this.userProfile.aeto_internal, () => html`
						<download-csv></download-csv>
					`)}
					<section class="container ${classMap({loggedIn: this.userProfile.loggedIn, loginFailed: this.userProfile.loginFailed})}">
						${when(this.userProfile.loggedIn, () => html`
							<div>${msg('Logged in:')} ${this.userProfile.userName}</div>
							<button class="submit" @click=${this.onLogout}>${msg('logout')}</button>
						`, () => html`
							<label class="field-name">${msg('Username')}
								<input type="text" name="email" id="email" .value=${this.email} @input=${this.handleInput} required="true">
							</label>
							<label class="field-name">${msg('Password')}
								<input type="password" name="password" id="password" @input=${this.handleInput} @keypress=${this.handleInput}  required="true">
							</label>

							<button ?disabled="${!loginEnabled}" class="submit" @click=${this.onLogin}>${msg('login')}</button>
						`)}
					</section>

				</div>
			</div>
		`;
	}


}
